import React from 'react'
import { Card, Container, InputGroup } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { Button, Column, Row, Select } from '../../components'
import { SelectOption } from '../../shared/components/input'
import { Labels } from './labels'
import { ICommissionReconciliationFilters } from './models/CommissionReconciliationTypes'

/** Filter row. Follows the Customer filter screen for layout and Select usage. */
interface Props {
  draftFilters: ICommissionReconciliationFilters
  onDraftChange: (next: ICommissionReconciliationFilters) => void
  onPreview: () => void
  onClear: () => void
  onExportCsv: () => void
  /** Nothing in the grid means nothing to export. */
  isExportDisabled: boolean
  carrierOptions: SelectOption[]
  policyTypeOptions: SelectOption[]
  agencyOptions: SelectOption[]
}

const CommissionReconciliationFilters: React.FunctionComponent<Props> = ({
  draftFilters,
  onDraftChange,
  onPreview,
  onClear,
  onExportCsv,
  isExportDisabled,
  carrierOptions,
  policyTypeOptions,
  agencyOptions,
}) => {
  const update = (patch: Partial<ICommissionReconciliationFilters>) =>
    onDraftChange({ ...draftFilters, ...patch })

  return (
    <Card>
      <Card.Body className="p-3" style={{ textAlign: 'left' }}>
        <Container fluid>
          <Row>
            <Column lg={3} md={4} sm={12}>
              <Select
                id="filter-carrier"
                label={Labels.FILTER_CARRIER}
                options={carrierOptions}
                inputProps={{ className: 'inputStyle', tabIndex: 1 }}
                defaultSelected={carrierOptions.filter(
                  ({ value }) => value === draftFilters.carrier,
                )}
                onChange={(values) => update({ carrier: values[0] as string })}
              />
            </Column>
            <Column lg={3} md={4} sm={12}>
              <Select
                id="filter-policy-type"
                label={Labels.FILTER_POLICY_TYPE}
                options={policyTypeOptions}
                inputProps={{ className: 'inputStyle', tabIndex: 2 }}
                defaultSelected={policyTypeOptions.filter(
                  ({ value }) => value === draftFilters.policyType,
                )}
                onChange={(values) => update({ policyType: values[0] as string })}
              />
            </Column>
            <Column lg={3} md={4} sm={12}>
              <Select
                id="filter-agency"
                label={Labels.FILTER_AGENCY}
                options={agencyOptions}
                inputProps={{ className: 'inputStyle', tabIndex: 3 }}
                defaultSelected={agencyOptions.filter(
                  ({ value }) => value === draftFilters.agency,
                )}
                onChange={(values) => update({ agency: values[0] as string })}
              />
            </Column>
            <Column lg={3} md={4} sm={12}>
              {/* Same InputGroup + DatePicker pattern as the Effective Date
                  field on CustomerBasicPolicyInfo. */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{Labels.FILTER_TERM_DATE}</InputGroup.Text>
                <DatePicker
                  dateFormatCalendar="LLLL yyyy"
                  tabIndex={4}
                  dropdownMode="scroll"
                  selected={draftFilters.termDate ?? null}
                  onChange={(date: Date | null) => update({ termDate: date })}
                  className="form-control"
                />
                <InputGroup.Text>
                  <i className="bi bi-calendar3"></i>
                </InputGroup.Text>
              </InputGroup>
            </Column>
          </Row>
          <Row>
            <Column>
              <div className="d-flex mb-2 mt-3">
                <Button
                  outlined
                  id="btnClear"
                  onClick={onClear}
                  className="me-2"
                >
                  {Labels.CLEAR}
                </Button>
                <Button id="btnPreview" onClick={onPreview} className="me-2">
                  {Labels.PREVIEW}
                </Button>
                <Button
                  outlined
                  id="btnExport"
                  onClick={onExportCsv}
                  disabled={isExportDisabled}
                >
                  {Labels.CSV_EXPORT}
                </Button>
              </div>
            </Column>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  )
}

export default CommissionReconciliationFilters
